import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { fetchTeamsData } from "../../Redux/Thunks/teamsThunk";
import {
  selectTeamsData,
  selectTeamsLoading,
  selectTeamsError,
} from "../../Redux/Slices/teamsSlice";
import {
  getTeamsListFromApiData,
  getTeamsMetaFromApiData,
  toTeamStatusApi,
} from "./teamsDataUtils";
import TeamList from "./TeamList";
import AddTeam from "./AddTeam";
import EditTeam from "./EditTeam";
import Typography from "../../Utility/Typography";
import AddButton from "../../Utility/AddButton/AddButton";
import { usePermissions } from "../../hooks/usePermissions";
import { PERMISSION_KEYS } from "../../permissions/permissionKeys";
import { TEAMS_LIST_PAGE_SIZE_STORAGE_KEY } from "../../Utility/Pagination/listPaginationConstants";
import { useListPageSize } from "../../hooks/useListPageSize";

const Teams = () => {
  const { pageSize: limit, setPageSize } = useListPageSize(TEAMS_LIST_PAGE_SIZE_STORAGE_KEY);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const teamsData = useSelector(selectTeamsData);
  const loading = useSelector(selectTeamsLoading);
  const error = useSelector(selectTeamsError);

  const list = useMemo(() => getTeamsListFromApiData(teamsData), [teamsData]);
  const meta = getTeamsMetaFromApiData(teamsData);

  const [searchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [isAddTeamModalOpen, setIsAddTeamModalOpen] = useState(false);
  const [teamToEdit, setTeamToEdit] = useState(null);
  const [listingFilter, setListingFilter] = useState({
    teamName: "",
    status: "",
  });
  const { can } = usePermissions();

  const fetchTeamsWithFilters = (page = 1, filterOverrides = null) => {
    const f = filterOverrides ?? listingFilter;
    const name = f.teamName?.trim() || undefined;
    const status = f.status && f.status !== "All" ? toTeamStatusApi(f.status) : undefined;
    dispatch(fetchTeamsData({ page, limit, name, status }));
  };

  useEffect(() => {
    dispatch(fetchTeamsData({ page: 1, limit }));
  }, [dispatch, limit]);

  const OpenModal = (modelID, data) => {
    // Modal handling - can be implemented later
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setListingFilter((prev) => ({ ...prev, [name]: value }));
  };

  const handleFilterReset = () => {
    setListingFilter({ teamName: "", status: "" });
    setCurrentPage(1);
    dispatch(fetchTeamsData({ page: 1, limit }));
  };

  const applyFilters = () => {
    setCurrentPage(1);
    fetchTeamsWithFilters(1);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    fetchTeamsWithFilters(page);
  };

  const handlePageSizeChange = (newLimit) => {
    setPageSize(newLimit);
    setCurrentPage(1);
    const f = listingFilter;
    const name = f.teamName?.trim() || undefined;
    const status = f.status && f.status !== "All" ? toTeamStatusApi(f.status) : undefined;
    dispatch(fetchTeamsData({ page: 1, limit: newLimit, name, status }));
  };

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <Typography variant="text1" className="text-red-600 text-lg">
            Error: {error}
          </Typography>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="w-full flex flex-col sm:flex-row items-center justify-between gap-2 py-3">
        <div className="flex flex-col gap-2">
          <div className="flex items-center gap-2">
            <Typography variant="text1" className="text-xl font-semibold">
              Teams
            </Typography>
          </div>
        </div>
        {can(PERMISSION_KEYS.TEAMS_CREATE) ? (
          <AddButton
            name="add team"
            onClick={() => setIsAddTeamModalOpen(true)}
            tooltip="Add Team"
          />
        ) : null}
      </div>

      <TeamList
        teamsData={{ list, meta }}
        loading={loading}
        searchTerm={searchTerm}
        currentPage={currentPage}
        setCurrentPage={handlePageChange}
        limit={limit}
        onPageSizeChange={handlePageSizeChange}
        OpenModal={OpenModal}
        listingFilter={listingFilter}
        handleFilterChange={handleFilterChange}
        handleFilterReset={handleFilterReset}
        applyFilters={applyFilters}
        onEditTeam={setTeamToEdit}
        onRefetchTeams={() => fetchTeamsWithFilters(currentPage)}
      />

      <AddTeam
        isOpen={isAddTeamModalOpen}
        onClose={() => setIsAddTeamModalOpen(false)}
        onSuccess={() => fetchTeamsWithFilters(currentPage)}
      />

      <EditTeam
        isOpen={!!teamToEdit}
        team={teamToEdit}
        onClose={() => setTeamToEdit(null)}
        onSuccess={() => {
          setTeamToEdit(null);
          fetchTeamsWithFilters(currentPage);
        }}
      />
    </div>
  );
};

export default Teams;
